import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { unsubscribePush } from './push';
import { api, onUnauthorized } from './api';

export interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: { key: string; name: string };
  permissions: string[];
  totpEnabled: boolean;
  mustChangePassword: boolean;
  tenantId: string | null;
  lastLoginAt: string | null;
  twoFactorRequired: boolean;
}

interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  login: (email: string, password: string, totp?: string) => Promise<{ requiresTotp?: boolean }>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const reload = useCallback(async () => {
    try {
      setUser(await api.get<CurrentUser>('/auth/me'));
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    return onUnauthorized(() => {
      setUser(null);
      qc.clear();
    });
  }, [reload, qc]);

  const login = useCallback(async (email: string, password: string, totp?: string) => {
    const r = await api.post<{ user?: CurrentUser; requiresTotp?: boolean }>('/auth/login', { email, password, totp });
    if (r.requiresTotp) return { requiresTotp: true };
    setUser(r.user ?? null);
    return {};
  }, []);

  const logout = useCallback(async () => {
    try {
      // Push-Abo dieses Geräts entfernen, damit nach dem Abmelden keine Meldungen mehr ankommen
      await unsubscribePush().catch(() => undefined);
      await api.post('/auth/logout');
    } finally {
      setUser(null);
      qc.clear();
    }
  }, [qc]);

  const can = useCallback(
    (permission: string) => {
      if (!user) return false;
      if (user.permissions.includes('*')) return permission !== 'portal.tenant';
      return user.permissions.includes(permission);
    },
    [user],
  );

  return <AuthContext.Provider value={{ user, loading, login, logout, reload, can }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const c = useContext(AuthContext);
  if (!c) throw new Error('AuthProvider fehlt');
  return c;
}

/** Rendert Kinder nur mit Berechtigung */
export function Can({ permission, children, fallback = null }: { permission: string; children: ReactNode; fallback?: ReactNode }) {
  const { can } = useAuth();
  return <>{can(permission) ? children : fallback}</>;
}
